import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useUserPreferences } from '../contexts/UserPreferencesContext';
import { usePlacesAutocomplete } from '../hooks/usePlacesAutocomplete';
import SettingsLayout from '../components/Settings/SettingsLayout';
import { dedupeByStoreName } from '../utils/deduplication';
import { FAVORITE_MODE_AUTO, FAVORITE_MODE_BRAND, FAVORITE_MODE_OPTIONS, FAVORITE_MODE_COPY } from '../utils/favorites';
import FavoriteModeHelp from '../components/Favorites/FavoriteModeHelp';
import './Profile.css';

export default function Profile() {
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const {
    favorites,
    addFavorite,
    updateFavorite,
    removeFavorite,
    petsFilter: petsFilterDefault,
    setPetsFilter: setPetsFilterDefault,
    jobTitle,
    setJobTitle,
  } = useUserPreferences();
  const [newStoreName, setNewStoreName] = useState('');
  const [favoriteMode, setFavoriteMode] = useState(FAVORITE_MODE_AUTO);
  const [showStoreDropdown, setShowStoreDropdown] = useState(false);
  const [highlightedStoreIndex, setHighlightedStoreIndex] = useState(-1);
  const storeInputWrapperRef = useRef(null);
  const storeListRef = useRef(null);

  function handlePetsFilterDefaultChange(value) {
    setPetsFilterDefault(value);
  }

  const storeAc = usePlacesAutocomplete(newStoreName);
  const favoriteModeCopy = FAVORITE_MODE_COPY[favoriteMode] || FAVORITE_MODE_COPY[FAVORITE_MODE_BRAND];
  const storeSuggestions = storeAc.suggestions;
  const storeSuggestionsDeduped = useMemo(() => dedupeByStoreName(storeSuggestions), [storeSuggestions]);
  const showStoreSuggestions =
    showStoreDropdown && (storeSuggestionsDeduped.length > 0 || storeAc.loading);

  useEffect(() => {
    setHighlightedStoreIndex(-1);
  }, [storeSuggestionsDeduped]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (storeInputWrapperRef.current?.contains(e.target)) return;
      setShowStoreDropdown(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (highlightedStoreIndex >= 0 && storeListRef.current) {
      const item = storeListRef.current.children[highlightedStoreIndex];
      item?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedStoreIndex]);

  function handleAddStoreFromSuggestion(suggestion) {
    const label = suggestion.structured_formatting?.main_text || suggestion.description || '';
    const trimmed = label.trim();
    if (!trimmed) return;
    addFavorite({
      label: trimmed,
      mode: favoriteMode,
      placeId: suggestion.place_id || '',
      query: trimmed,
      description: suggestion.structured_formatting?.secondary_text || '',
    });
    setNewStoreName('');
    setShowStoreDropdown(false);
    setHighlightedStoreIndex(-1);
    storeAc.resetSession();
  }

  function handleAddManual(e) {
    e.preventDefault();
    const trimmed = newStoreName.trim();
    if (!trimmed) return;
    addFavorite({
      label: trimmed,
      mode: favoriteMode,
      query: trimmed,
    });
    setNewStoreName('');
    setShowStoreDropdown(false);
  }

  function handleStoreKeyDown(e) {
    if (!showStoreSuggestions || storeSuggestionsDeduped.length === 0) {
      if (e.key === 'Escape') setShowStoreDropdown(false);
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedStoreIndex((i) =>
          i < storeSuggestionsDeduped.length - 1 ? i + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedStoreIndex((i) =>
          i > 0 ? i - 1 : storeSuggestionsDeduped.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedStoreIndex >= 0 && storeSuggestionsDeduped[highlightedStoreIndex]) {
          handleAddStoreFromSuggestion(storeSuggestionsDeduped[highlightedStoreIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowStoreDropdown(false);
        setHighlightedStoreIndex(-1);
        break;
      default:
        break;
    }
  }

  if (authLoading) {
    return (
      <SettingsLayout activeTab="profile">
        <div className="profile-page">
          <p className="profile-page__loading">Loading…</p>
        </div>
      </SettingsLayout>
    );
  }

  if (!user) {
    return <Navigate to="/signin" state={{ from: location }} replace />;
  }

  return (
    <SettingsLayout activeTab="profile">
      <div className="profile-page">
        <div className="profile-page__card">
          <header className="profile-page__header">
            <h1 className="profile-page__title">Profile</h1>
            <p className="profile-page__email">{user.email}</p>
          </header>

          <section className="profile-page__section" aria-labelledby="job-title-heading">
            <h2 id="job-title-heading" className="profile-page__heading">Job title</h2>
            <p className="profile-page__hint">
              Your job title personalizes the licensing resources shown on the search page when you look up a new location.
            </p>
            <div className="profile-page__pets-wrap">
              <label htmlFor="profile-job-title" className="profile-page__pets-label">Title</label>
              <select
                id="profile-job-title"
                className="profile-page__pets-select"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                aria-label="Your travel healthcare job title"
              >
                <option value="">-- Select a job title --</option>
                <option value="travel_nurse">Travel Nurse</option>
                <option value="travel_ir_tech">Travel Interventional Radiologic Technologist</option>
              </select>
            </div>
          </section>

          <section className="profile-page__section" aria-labelledby="pets-default-heading">
            <h2 id="pets-default-heading" className="profile-page__heading">
              Default pets filter
            </h2>
            <p className="profile-page__hint">
              When you search, Furnished Finder listings can be filtered by whether they allow pets. This is the default filter when you open the search page.
            </p>
            <div className="profile-page__pets-wrap">
              <label htmlFor="profile-pets-filter" className="profile-page__pets-label">
                Pets
              </label>
              <select
                id="profile-pets-filter"
                className="profile-page__pets-select"
                value={petsFilterDefault}
                onChange={(e) => handlePetsFilterDefaultChange(e.target.value)}
                aria-label="Default filter for properties by pets allowed"
              >
                <option value="all">Show both</option>
                <option value="pets">Pets allowed</option>
                <option value="no-pets">No pets</option>
              </select>
            </div>
          </section>

        <section className="profile-page__section" aria-labelledby="favorite-stores-heading">
          <h2 id="favorite-stores-heading" className="profile-page__heading">
            Favorite places
          </h2>
          <p className="profile-page__hint">
            Add favorite places and choose whether they should behave like a brand / chain or something broad when you search.
          </p>

          <form className="profile-page__add-form" onSubmit={handleAddManual}>
            <label htmlFor="profile-new-store" className="profile-page__label profile-page__label--with-help">
              <span>Add location</span>
              <FavoriteModeHelp />
            </label>
            <div
              className="profile-page__add-row profile-page__add-row--autocomplete"
              ref={storeInputWrapperRef}
            >
              <div className="profile-page__input-wrap">
                <input
                  id="profile-new-store"
                  type="text"
                  role="combobox"
                  className={`profile-page__input ${storeAc.loading ? 'profile-page__input--loading' : ''}`}
                  value={newStoreName}
                  onChange={(e) => {
                    setNewStoreName(e.target.value);
                    setShowStoreDropdown(true);
                  }}
                  onFocus={() => setShowStoreDropdown(true)}
                  onKeyDown={handleStoreKeyDown}
                  placeholder={favoriteModeCopy.placeholder}
                  autoComplete="off"
                  aria-autocomplete="list"
                  aria-expanded={showStoreSuggestions}
                  aria-controls="profile-store-suggestions"
                />
                {storeAc.loading && (
                  <span className="profile-page__input-spinner" aria-hidden="true" />
                )}
                {showStoreSuggestions && (
                  <ul
                    id="profile-store-suggestions"
                    className="profile-page__suggestions"
                    ref={storeListRef}
                    role="listbox"
                  >
                    {storeAc.loading && storeSuggestionsDeduped.length === 0 ? (
                      <li className="profile-page__suggestion profile-page__suggestion--loading">
                        Searching…
                      </li>
                    ) : (
                      storeSuggestionsDeduped.map((suggestion, i) => (
                        <li
                          key={`${(suggestion.structured_formatting?.main_text || suggestion.description || '').trim().toLowerCase()}-${i}`}
                          className={`profile-page__suggestion ${
                            i === highlightedStoreIndex ? 'profile-page__suggestion--highlighted' : ''
                          }`}
                          role="option"
                          aria-selected={i === highlightedStoreIndex}
                          onMouseEnter={() => setHighlightedStoreIndex(i)}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleAddStoreFromSuggestion(suggestion);
                          }}
                        >
                          <span className="profile-page__suggestion-main">
                            {suggestion.structured_formatting?.main_text || suggestion.description}
                          </span>
                        </li>
                      ))
                    )}
                  </ul>
                )}
              </div>
              <select
                className="profile-page__pets-select"
                value={favoriteMode}
                onChange={(e) => setFavoriteMode(e.target.value)}
                aria-label="Favorite mode"
              >
                {FAVORITE_MODE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              <button type="submit" className="profile-page__btn profile-page__btn--add">
                Add
              </button>
            </div>
          </form>

          <p className="profile-page__hint">{favoriteModeCopy.examples}</p>
          {favorites.length === 0 ? (
            <p className="profile-page__empty">No favorites yet. Type a place or broad term above, choose a mode if you want, and click Add.</p>
          ) : (
            <ul className="profile-page__list">
              {favorites.map((favorite) => (
                <li key={`${favorite.mode}-${favorite.placeId || favorite.label}`} className="profile-page__item">
                  <div className="profile-page__item-main">
                    <span className="profile-page__item-name">{favorite.label}</span>
                  </div>
                  <select
                    className="profile-page__favorite-mode-select"
                    value={favorite.mode}
                    onChange={(e) => updateFavorite(favorite, { mode: e.target.value })}
                    aria-label={`Favorite mode for ${favorite.label}`}
                  >
                    {FAVORITE_MODE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="profile-page__btn profile-page__btn--remove"
                    onClick={() => removeFavorite(favorite)}
                    aria-label={`Remove ${favorite.label}`}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        </div>
      </div>
    </SettingsLayout>
  );
}
